import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AnnualReportTemplate } from '@vi-iot/client/apis/annual-report/annual-report.model';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import {
  DiagnosisExplanation,
  InstallationRecommendation,
  RemoteServiceFeature,
} from '@vi-iot/client';

import { DevicesStore, RichDevice } from '@store';

import { RemoteServiceOverlayTypeEnum } from './../../+installations/installation/installation-overlay.model';

import { RemoteServiceActions } from './remote-service.actions';
import { RemoteServiceDiagnosisEntry } from './remote-service.model';
import { RemoteServiceSelectors } from './remote-service.selectors';

@Injectable({
  providedIn: 'root',
})
export class RemoteServiceStore {
  chartDataStatus$ = this.store.select(RemoteServiceSelectors.getChartDataStatus());
  diagnosisStatus$ = this.store.select(RemoteServiceSelectors.getDiagnosisStatus());
  thresholdsStatus$ = this.store.select(RemoteServiceSelectors.getThresholdsStatus());
  annualReportState$ = this.store.select(RemoteServiceSelectors.getAnnualReportState());
  selectedDiagnosisEntry$ = this.store.select(RemoteServiceSelectors.getSelectedDiagnosisEntry());
  chartsConfig$ = this.store.select(RemoteServiceSelectors.getChartsConfig());
  chartTimestamps$ = this.store.select(RemoteServiceSelectors.getChartTimestamps());
  chartExtremes$ = this.store.select(RemoteServiceSelectors.getChartExtremes());
  diagnosisList$ = this.store.select(RemoteServiceSelectors.selectGetDiagnosisListData);
  anomalyList$ = this.store.select(RemoteServiceSelectors.selectGetAnomalyListData);
  diagnosisCalculationDataStatus$ = this.store.select(
    RemoteServiceSelectors.selectDiagnosisCalculationDataStatus
  );
  chartFeatures$ = this.store.select(RemoteServiceSelectors.getChartsFeatures());
  remoteServiceFeaturesLoadingStatus$ = this.store.select(
    RemoteServiceSelectors.getRemoteServiceFeaturesLoadingStatus
  );
  remoteServiceDiagnosisLoadingStatus$ = this.store.select(
    RemoteServiceSelectors.getRemoteServiceDiagnosisLoadingStatus
  );
  remoteServiceDiagnosisChartLoadingStatus$ = this.store.select(
    RemoteServiceSelectors.remoteServiceDiagnosisChartLoadingStatus
  );
  canDisplayNoAnomaliesInformation$ = this.store.select(
    RemoteServiceSelectors.getCanDisplayNoAnomaliesInformation
  );
  featuresChartsDetails$ = this.store.select(RemoteServiceSelectors.getFeaturesChartsDetails);
  featuresChartSeries$ = this.store.select(RemoteServiceSelectors.getFeaturesChartSeries);
  ignoredRemoteServiceNotifications$ = this.store.select(
    RemoteServiceSelectors.ignoredRemoteServiceNotifications
  );
  isLowBudgetNotificationVisible$ = this.store.select(
    RemoteServiceSelectors.isLowBudgetNotificationVisible
  );
  selectedDevice$ = this.store.select(RemoteServiceSelectors.selectSelectedDevice);
  diagnosisWhitelist$ = this.store.select(RemoteServiceSelectors.selectDiagnosisWhitelist);
  isDiagnosisWhitelistLoaded$ = this.store.select(
    RemoteServiceSelectors.selectIsDiagnosisWhitelistLoaded
  );

  constructor(private store: Store, private devicesStore: DevicesStore) {}

  selectDiagnosisEntry(diagnosisEntry: RemoteServiceDiagnosisEntry): void {
    this.store.dispatch(RemoteServiceActions.selectDiagnosisEntry({ diagnosisEntry }));
  }

  selectFirstDiagnosisEntry(): void {
    this.store.dispatch(RemoteServiceActions.selectFirstDiagnosisEntry());
  }

  selectFirstAnomalyEntry(overwrite = true): void {
    this.store.dispatch(RemoteServiceActions.selectFirstAnomalyEntry({ overwrite }));
  }

  loadChartsConfig(): void {
    this.store.dispatch(RemoteServiceActions.loadChartsConfig());
  }

  loadChartsFeatures(deviceSerial: string = null): void {
    this.store.dispatch(RemoteServiceActions.loadChartFeatures({ deviceSerial }));
  }

  loadDiagnosisData(deviceSerial: string = null): void {
    this.store.dispatch(RemoteServiceActions.loadDiagnosis({ deviceSerial }));
  }

  setNewChartExtremes(startPointMs: number, endPointMs: number): void {
    this.store.dispatch(
      RemoteServiceActions.setChartExtremes({
        startPointMs,
        endPointMs,
      })
    );
  }

  getChartFeatures(validFeatures: string[]): Observable<RemoteServiceFeature[]> {
    return this.store.select(RemoteServiceSelectors.getChartsFeatures()).pipe(
      filter((chartsData) => chartsData?.series.length > 0),
      map((chartsData) =>
        chartsData.series.filter((entry) => validFeatures.includes(entry.feature))
      ),
      filter((features) => features.length > 0)
    );
  }

  selectInstallationRecommendation(boilerSerial: string): Observable<InstallationRecommendation> {
    return this.store.select(RemoteServiceSelectors.getInstallationRecommendation(boilerSerial));
  }

  saveThreshold(group: string, name: string, value: number): void {
    this.devicesStore.mainDeviceBoilerSerial$
      .pipe(
        filter((entry) => !!entry),
        take(1)
      )
      .subscribe((mainDeviceBoilerSerial) => {
        this.store.dispatch(
          RemoteServiceActions.saveThreshold({
            params: { boiler_id: mainDeviceBoilerSerial, group, name, value, comment: '' },
          })
        );
      });
  }

  clearState(): void {
    this.store.dispatch(RemoteServiceActions.clearState());
  }

  clearDetailedInspectionState(): void {
    this.store.dispatch(RemoteServiceActions.clearDetailedInspectionState());
  }

  getReport(
    reportTemplate: AnnualReportTemplate,
    gatewaySerial: string,
    deviceSerial: string
  ): void {
    this.store.dispatch(
      RemoteServiceActions.getAnnualReport({ reportTemplate, gatewaySerial, deviceSerial })
    );
  }

  tabChanged(tabType: RemoteServiceOverlayTypeEnum): void {
    this.store.dispatch(RemoteServiceActions.tabChanged({ tabType }));
  }

  getLowBudgetNotification(): void {
    this.store.dispatch(RemoteServiceActions.getLowBudgetNotification());
  }

  hideLowBudgetNotification(): void {
    this.store.dispatch(RemoteServiceActions.hideLowBudgetNotification());
  }

  changeSelectedDevice(device: RichDevice): void {
    this.store.dispatch(RemoteServiceActions.selectDevice({ device }));
  }

  loadDiagnosisWhitelist(boilerId: string): void {
    this.store.dispatch(RemoteServiceActions.loadDiagnosisWhitelist.attempt({ boilerId }));
  }

  loadDiagnosisExplanation(
    diagnosisName: string,
    boilerId: string,
    lang: string,
    startDatetime: string,
    endDatetime: string
  ): void {
    this.store.dispatch(
      RemoteServiceActions.loadDiagnosisExplanation.request({
        diagnosisName,
        boilerId,
        lang,
        startDatetime,
        endDatetime,
      })
    );
  }

  getDiagnosisExplanation(diagnosisName: string): Observable<DiagnosisExplanation | null> {
    return this.store.select(RemoteServiceSelectors.selectDiagnosisExplanation(diagnosisName));
  }

  isDiagnosisExplanationLoading(diagnosisName: string): Observable<boolean> {
    return this.store.select(
      RemoteServiceSelectors.selectIsDiagnosisExplanationLoading(diagnosisName)
    );
  }

  isDiagnosisInWhitelist(diagnosisName: string): Observable<boolean> {
    return this.store.select(RemoteServiceSelectors.selectIsDiagnosisInWhitelist(diagnosisName));
  }
}
